import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Avatar, Button, message } from "antd";
import {
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  KeyOutlined,
  LogoutOutlined,
  MailOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { colors } from "../../Utils/temas";
import { ModalTrocarSenha } from "./componentes/modal/modalTrocarSenha";
import { ModalSenhaTri7 } from "./componentes/modal/modalSenhaTri7";
import { ModalQrCodeTri7 } from "./componentes/modal/modalQrCodeTri7";
import "./estilo.css";
import { useUserContext } from "../../contexts/usersContext";
import { ModalAutenticador } from "./componentes/modalAutenticador";
import { GrupoMenu, ItemMenu } from "./componentes/itensMenu";
import { Usuario } from "./perfil";

const MenuSuperior = () => {
  const [open, setOpen] = useState(false);
  const [openChange, setOpenChange] = useState(false);
  const [openAuthenticator, setOpenAuthenticator] = useState(false);
  const [openTri7Password, setOpenTri7Password] = useState(false);
  const [openTri7QrCode, setOpenTri7QrCode] = useState(false);
  const [openHelpSystemMenu, setOpenHelpSystemMenu] = useState(false);
  const [openTri7PasswordMenu, setOpenTri7PasswordMenu] = useState(false);
  const [loadingSendTri7Password, setLoadingSendTri7Password] = useState(false);
  const [showTokenPanel, setShowTokenPanel] = useState(false);
  const [token, setToken] = useState("");
  const [tempoRestante, setTempoRestante] = useState(0);
  const [loadingTri7Token, setLoadingTri7Token] = useState(false);
  const [menuHeight, setMenuHeight] = useState(0);
  const menuRef = useRef(null);
  const intervaloTokenRef = useRef(null);
  const timeoutRenovacaoRef = useRef(null);
  const renovacaoDisparadaNoZeroRef = useRef(false);

  const navigate = useNavigate();

  const { logout, currentUser } = useUserContext();
  const showTri7Menu = Boolean(currentUser?.habilitar_tri7);

  const sairSistema = () => {
    logout();
    navigate("/");
    window.location.reload(false);
  };

  const limparTimerToken = useCallback(() => {
    if (intervaloTokenRef.current) {
      clearInterval(intervaloTokenRef.current);
      intervaloTokenRef.current = null;
    }
    if (timeoutRenovacaoRef.current) {
      clearTimeout(timeoutRenovacaoRef.current);
      timeoutRenovacaoRef.current = null;
    }
  }, []);

  const buscarTokenTri7 = useCallback(async () => {
    setLoadingTri7Token(true);

    try {
      const response = await Usuario.getBuscarTokenTri7();

      if (response.status === 200) {
        const { totp, tempo_restante_segundos } = response.data;

        setToken(totp);
        setTempoRestante(tempo_restante_segundos);
      }
    } catch {
      message.error("Não foi possível buscar o token Tri7.");
      setShowTokenPanel(false);
      limparTimerToken();
    } finally {
      setLoadingTri7Token(false);
    }
  }, [limparTimerToken]);

  const onToggleTokenPanel = async (event) => {
    event.stopPropagation();

    if (showTokenPanel) {
      setShowTokenPanel(false);
      limparTimerToken();
      return;
    }

    setShowTokenPanel(true);
    await buscarTokenTri7();
  };

  const enviarSenhaTri7 = async () => {
    setLoadingSendTri7Password(true);

    try {
      const response = await Usuario.postEnviarSenhaTri7();

      if (response.status === 200 || response.status === 201) {
        message.success("Senha Tri7 enviada por e-mail com sucesso!");
        setOpen(false);
      }
    } catch {
      message.error("Opa, ocorreu um erro ao enviar a Senha Tri7.");
    } finally {
      setLoadingSendTri7Password(false);
    }
  };

  // A altura sai do conteudo, nao de um numero de itens vezes uma altura fixa:
  // o padding dos botoes nao batia com a estimativa e a ultima opcao ficava
  // cortada, e cada opcao nova exigia lembrar de atualizar a conta.
  useLayoutEffect(() => {
    const menu = menuRef.current;

    if (!open || !menu) {
      setMenuHeight(0);
      return;
    }

    // Medir com o menu ainda recolhido daria zero: os botoes sao itens flex e
    // encolhem junto com o container. Abrir a altura antes de ler resolve --
    // em useLayoutEffect isso acontece antes da pintura, sem piscar na tela.
    const alturaAnterior = menu.style.height;
    menu.style.height = "auto";
    const alturaDoConteudo = menu.offsetHeight;
    menu.style.height = alturaAnterior;

    setMenuHeight(alturaDoConteudo);
  }, [
    open,
    openHelpSystemMenu,
    openTri7PasswordMenu,
    showTri7Menu,
    currentUser.otp_enabled,
  ]);

  useEffect(() => {
    if (!showTokenPanel || tempoRestante <= 0 || intervaloTokenRef.current) {
      return;
    }

    intervaloTokenRef.current = setInterval(() => {
      setTempoRestante((previous) => {
        if (previous <= 1) {
          return 0;
        }

        return previous - 1;
      });
    }, 1000);

    return () => {
      if (intervaloTokenRef.current) {
        clearInterval(intervaloTokenRef.current);
        intervaloTokenRef.current = null;
      }
    };
  }, [showTokenPanel, tempoRestante]);

  useEffect(() => {
    if (!showTokenPanel) {
      limparTimerToken();
      renovacaoDisparadaNoZeroRef.current = false;
      return;
    }

    if (tempoRestante > 0) {
      renovacaoDisparadaNoZeroRef.current = false;
    }

    const deveRenovarNoZero =
      tempoRestante === 0 && !renovacaoDisparadaNoZeroRef.current;

    if (deveRenovarNoZero && !loadingTri7Token) {
      renovacaoDisparadaNoZeroRef.current = true;
      limparTimerToken();
      timeoutRenovacaoRef.current = setTimeout(() => {
        if (showTokenPanel) {
          buscarTokenTri7();
        }
      }, 1000);
    }
  }, [
    buscarTokenTri7,
    limparTimerToken,
    showTokenPanel,
    tempoRestante,
    loadingTri7Token,
  ]);

  useEffect(() => {
    return () => limparTimerToken();
  }, [limparTimerToken]);

  return (
    <div className="menuSuperiorWrapper">
      {showTri7Menu && (
        <div className="tri7TokenContainer">
          <Button
            type="text"
            className="tri7TokenButton"
            onClick={onToggleTokenPanel}
            icon={
              showTokenPanel ? (
                <EyeOutlined style={{ color: "#1f4a4d", fontSize: 20 }} />
              ) : (
                <EyeInvisibleOutlined
                  style={{ color: "#1f4a4d", fontSize: 20 }}
                />
              )
            }
          />

          <div className="tri7TokenInline">
            <span className="tri7TokenTitle">Token Tri7</span>
            
            <div className="tri7TokenRow">
              <span className="tri7TokenLabel">Token:</span>
              <span
                className={`tri7TokenValue ${!showTokenPanel ? "tri7BlurredValue" : ""}`}
              >
                {token || "-"}
              </span>
            </div>
            <div className="tri7TokenRow">
              <span className="tri7TokenLabel">Tempo:</span>
              <span
                className={`tri7TokenValue ${!showTokenPanel ? "tri7BlurredValue" : ""}`}
              >
                {`${tempoRestante}s`}
              </span>
            </div>
          </div>
        </div>
      )}

      <Button
        onClick={() => setOpen(!open)}
        type="link"
        className="profileMenuButton"
        icon={
          <div
            style={{
              background: open ? colors.terciary : "transparent",
              transition: "background 0.2s",
              borderRadius: "100%",
              width: 44,
              height: 43.4,
              padding: "1.8px 1.8px 1.8px 2px",
            }}
          >
            <Avatar
              icon={
                <text style={{ fontWeight: "500" }}>
                  {currentUser.nome[0].toLocaleUpperCase()}
                </text>
              }
              style={{
                background: colors.secondary,
                border: "2px solid white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              size={40}
            />
          </div>
        }
        style={{ alignItems: "center" }}
      />

      <div
        style={{
          position: "fixed",
          background: "red",
          height: open ? "100%" : 0,
          left: 0,
          top: 0,
          right: 6,
          bottom: 0,
          zIndex: 600,
          backgroundColor: "transparent",
          display: "flex",
          justifyContent: "flex-end",
        }}
        onClick={() => setOpen(false)}
      >
        <div
          ref={menuRef}
          style={{
            display: "flex",
            flexDirection: "column",
            position: "absolute",
            background: colors.primary,
            borderRadius: 4,
            zIndex: 2,
            alignItems: "flex-start",
            transition: "height 0.08s, width 0.1s linear",
            height: menuHeight,
            overflow: "hidden",
            marginTop: 60,
          }}
        >
          <ItemMenu
            icone={LogoutOutlined}
            rotulo="Sair da conta"
            separador={false}
            onClick={sairSistema}
          />

          <GrupoMenu
            icone={KeyOutlined}
            rotulo="Senha HelpSystem"
            aberto={openHelpSystemMenu}
            onToggle={() => setOpenHelpSystemMenu(!openHelpSystemMenu)}
          >
            <ItemMenu
              aninhado
              icone={EditOutlined}
              rotulo="Alterar Senha"
              onClick={() => setOpenChange(true)}
            />

            {currentUser.otp_enabled && (
              <ItemMenu
                aninhado
                icone={QrcodeOutlined}
                rotulo="QR Code"
                onClick={() => setOpenAuthenticator(true)}
              />
            )}
          </GrupoMenu>

          {showTri7Menu && (
            <GrupoMenu
              icone={KeyOutlined}
              rotulo="Senha Tri7"
              aberto={openTri7PasswordMenu}
              onToggle={() => setOpenTri7PasswordMenu(!openTri7PasswordMenu)}
            >
              <ItemMenu
                aninhado
                icone={EditOutlined}
                rotulo="Alterar Senha"
                onClick={() => setOpenTri7Password(true)}
              />

              <ItemMenu
                aninhado
                icone={MailOutlined}
                rotulo="Enviar Senha"
                loading={loadingSendTri7Password}
                onClick={(event) => {
                  event.stopPropagation();
                  enviarSenhaTri7();
                }}
              />

              <ItemMenu
                aninhado
                icone={QrcodeOutlined}
                rotulo="QR Code"
                onClick={() => setOpenTri7QrCode(true)}
              />
            </GrupoMenu>
          )}
        </div>
      </div>

      <ModalTrocarSenha
        onCancel={() => setOpenChange(false)}
        visible={openChange}
      />
      <ModalAutenticador
        onCancel={() => setOpenAuthenticator(false)}
        visible={openAuthenticator}
      />
      <ModalSenhaTri7
        onCancel={() => setOpenTri7Password(false)}
        visible={openTri7Password}
      />
      <ModalQrCodeTri7
        onCancel={() => setOpenTri7QrCode(false)}
        visible={openTri7QrCode}
      />
    </div>
  );
};

export default MenuSuperior;
